import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import readXlsxFile from "read-excel-file";

import { history } from "../../utils/History";
import { isSafeListReturnPath } from "../../utils/listQueryState";
import { getRequestErrorMessage } from "../../utils/winnerWorkflow";
import { toastError } from "../../components/commons/toast";
import Promo from "../../store/actions/promotion/Index";
import WinnerReview from "./WinnerReview";

const getPromoDetail = (dispatch, promotionId) =>
  new Promise((resolve) => {
    dispatch(Promo.detail({ id: promotionId }, resolve));
  }).then((response) => response?.data);

const getWinnerPage = (dispatch, params) =>
  new Promise((resolve, reject) => {
    dispatch(Promo.getWinner(params, resolve, reject));
  });

const getAllExistingWinners = async (dispatch, promotionId) => {
  const length = 250;
  let page = 1;
  let winners = [];
  let total = 0;

  do {
    const response = await getWinnerPage(dispatch, {
      page,
      length,
      id: promotionId,
      status: "",
      search_text: "",
      city_id: "",
      store: "",
      silent: true,
    });
    const pageData = response?.data ?? [];
    winners = winners.concat(pageData);
    total = response?.totalCount ?? winners.length;
    if (!pageData.length && winners.length < total) {
      throw new Error("Daftar pemenang lama tidak lengkap.");
    }
    page += 1;
  } while (winners.length < total);

  return winners;
};

const readVoucherCodes = async (fileUrl) => {
  if (!fileUrl) throw new Error("File voucher promo tidak tersedia.");
  const url = /^https?:\/\//i.test(fileUrl)
    ? fileUrl
    : `${process.env.REACT_APP_API_URL}${fileUrl}`;
  const response = await fetch(url);
  if (!response.ok) throw new Error("File voucher promo gagal dimuat.");
  const blob = await response.blob();
  const rows = await readXlsxFile(new File([blob], "voucher.xlsx"));
  return rows
    .slice(1)
    .map((row) => String(row?.[1] ?? "").trim())
    .filter(Boolean);
};

const RegenerateWinnerResult = ({ match }) => {
  const promotionId = match?.params?.id_promo;
  const location = useLocation();
  const dispatch = useDispatch();
  const generatedWinners = location.state?.newData;
  const [promo, setPromo] = useState();
  const [preparedWinners, setPreparedWinners] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const winnerPath = `/promo-winner/${promotionId}`;
  const requestedReturnTo = location.state?.returnTo;
  const backPath = isSafeListReturnPath(requestedReturnTo, winnerPath)
    ? requestedReturnTo
    : `${winnerPath}${location.search}`;

  useEffect(() => {
    if (!Array.isArray(generatedWinners)) {
      toastError("Hasil generate tidak tersedia.");
      history.replace(backPath);
      return undefined;
    }

    let active = true;
    const prepareReview = async () => {
      setIsLoading(true);
      setLoadError("");
      try {
        const promoData = await getPromoDetail(dispatch, promotionId);
        if (!active) return;
        setPromo(promoData);

        const [existingWinners, voucherCodes] = await Promise.all([
          getAllExistingWinners(dispatch, promotionId),
          readVoucherCodes(promoData?.file_url),
        ]);
        if (!active) return;

        const usedCodes = new Set(
          existingWinners
            .filter((winner) => Number(winner.status) !== 2)
            .map((winner) => String(winner.voucher_code ?? "").trim().toLowerCase())
            .filter(Boolean)
        );
        const availableCodes = voucherCodes.filter(
          (code) => !usedCodes.has(code.toLowerCase())
        );

        if (availableCodes.length < generatedWinners.length) {
          const shortage = generatedWinners.length - availableCodes.length;
          throw new Error(
            `Voucher tersedia kurang ${shortage}. Tambahkan voucher sebelum melanjutkan.`
          );
        }

        setPreparedWinners(
          generatedWinners.map((winner, index) => ({
            ...winner,
            voucher_id: availableCodes[index],
            id_voucher: availableCodes[index],
          }))
        );
      } catch (error) {
        if (!active) return;
        const message = getRequestErrorMessage(
          error,
          "Hasil generate ulang gagal disiapkan."
        );
        setLoadError(message);
        toastError(message);
      } finally {
        if (active) setIsLoading(false);
      }
    };

    prepareReview();
    return () => {
      active = false;
    };
  }, [backPath, dispatch, generatedWinners, promotionId]);

  if (!Array.isArray(generatedWinners)) return null;

  return (
    <WinnerReview
      promotionId={promotionId}
      promo={promo}
      winners={preparedWinners}
      isLoading={isLoading}
      loadError={loadError}
      backPath={backPath}
      title="Hasil Generate Ulang Pemenang"
    />
  );
};

export default RegenerateWinnerResult;
